import React, { useEffect, useState, useRef } from "react";
import * as d3 from "d3";
import { useIntl } from "react-intl";
import { AppContext } from "../../contexts/AppContext";
import Box from "@mui/material/Box";
import { StravaActivityStreams } from "../../services/StravaService";
import { convertDistance } from "../../utils/UnitsUtils";
import { Plan, PlannedEffort, SectionData } from "../../services/PlansService";

interface TimeAccumulationPlotProps {
    id: string;
    activityStreams: StravaActivityStreams;
    sectionsData?: SectionData[];
    plan: Plan;
    dimensions: {
        width: number;
        height: number;
        margin: { top: number; right: number; bottom: number; left: number };
    };
    setHoveredDistance?: (distance: number | null) => void;
}

const TimeAccumulationPlot: React.FC<TimeAccumulationPlotProps> = ({
    id,
    activityStreams,
    sectionsData,
    plan,
    dimensions,
    setHoveredDistance,
}) => {
    const intl = useIntl();
    const { userService } = React.useContext(AppContext);

    const distanceUnits = userService.user?.distanceUnits || "km";
    const svgRef = useRef(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const { width: minWidth, height, margin } = dimensions;
    const marginBottom = Math.min(margin.bottom, 20);

    const [width, setWidth] = useState(
        containerRef.current?.clientWidth || minWidth - margin.left - margin.right
    );

    const formatTime = (seconds: number): string => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = Math.floor(seconds % 60);

        if (hours > 0) {
            return `${hours}h ${minutes < 10 ? '0' : ''}${minutes}min`;
        }
        return `${minutes}min ${secs < 10 ? '0' : ''}${secs}s`;
    };

    const prepareActivityData = (): { distance: number; accumulatedTime: number }[] => {
        if (!activityStreams || !activityStreams.distance || !activityStreams.time) {
            return [];
        }

        return activityStreams.distance.map((dist, i) => ({
            distance: dist,
            accumulatedTime: activityStreams.time[i]
        }));
    };

    const preparePlanData = (effort: PlannedEffort[]): { distance: number; accumulatedTime: number }[] => {
        if (!effort || effort.length === 0 || !sectionsData || sectionsData.length === 0) {
            return [];
        }

        const result = [];
        let accumulatedTime = 0;
        let lastDistance = 0;

        result.push({
            distance: 0,
            accumulatedTime: 0
        });

        for (let i = 0; i < sectionsData.length; i++) {
            const section = sectionsData[i];

            if (!section || !effort[i]) continue;
            accumulatedTime += effort[i].time * 60;
            lastDistance += section.distance;

            result.push({
                distance: lastDistance,
                accumulatedTime
            });
        }

        return result;
    };

    const prepareAltitudeData = (): { distance: number; altitude: number }[] => {
        if (!activityStreams || !activityStreams.distance || !activityStreams.altitude) {
            return [];
        }

        return activityStreams.distance.map((dist, i) => ({
            distance: dist,
            altitude: activityStreams.altitude![i] || 0
        }));
    };

    const activityData = prepareActivityData();
    const realisticData = plan?.plannedEffort?.length ? preparePlanData(plan.plannedEffort) : [];
    const pessimisticData = plan?.pessimisticEffort?.length ? preparePlanData(plan.pessimisticEffort) : [];
    const optimisticData = plan?.optimisticEffort?.length ? preparePlanData(plan.optimisticEffort) : [];
    const altitudeData = prepareAltitudeData();

    useEffect(() => {
        setWidth(containerRef.current?.clientWidth || minWidth - margin.left - margin.right);

        const allPlanData = [...realisticData, ...pessimisticData, ...optimisticData];

        if (activityData.length === 0 || allPlanData.length === 0) return;

        const maxDistance = Math.max(
            d3.max(activityData, d => d.distance) || 0,
            d3.max(allPlanData, d => d.distance) || 0
        );

        const maxTime = Math.max(
            d3.max(activityData, d => d.accumulatedTime) || 0,
            d3.max(allPlanData, d => d.accumulatedTime) || 0
        );

        const x = d3.scaleLinear()
            .domain([0, maxDistance])
            .range([margin.left, width - margin.right]);

        const y = d3.scaleLinear()
            .domain([0, maxTime * 1.05])
            .range([height - marginBottom, margin.top]);

        const svg = d3.select(svgRef.current);

        svg.selectAll("*").remove();

        svg.append("clipPath")
            .attr("id", `clip-time-${id}`)
            .append("rect")
            .attr("width", width)
            .attr("height", height - marginBottom);

        if (altitudeData.length > 0) {
            const minAltitude = d3.min(altitudeData, d => d.altitude) || 0;
            const maxAltitude = d3.max(altitudeData, d => d.altitude) || 0;
            const altitudeRange = maxAltitude - minAltitude;

            const yAltitude = d3.scaleLinear()
                .domain([minAltitude, maxAltitude + altitudeRange * 0.1])
                .range([height - marginBottom, margin.top]);

            const altitudeArea = d3.area<any>()
                .x(d => x(d.distance))
                .y0(height - marginBottom)
                .y1(d => yAltitude(d.altitude))
                .curve(d3.curveBasis);

            svg.append("path")
                .datum(altitudeData)
                .attr("fill", "#e6e6e6")
                .attr("fill-opacity", 0.5)
                .attr("d", altitudeArea);

            if (altitudeRange > 50) {
                const altitudeAxisRight = svg.append("g")
                    .attr("class", "altitude-axis")
                    .attr("transform", `translate(${width - margin.right + 5},0)`);

                const altitudeTicks = yAltitude.ticks(5);

                altitudeAxisRight.selectAll(".altitude-tick")
                    .data(altitudeTicks)
                    .enter()
                    .append("text")
                    .attr("class", "altitude-tick")
                    .attr("x", 0)
                    .attr("y", d => yAltitude(d))
                    .attr("dx", "0.35em")
                    .attr("text-anchor", "start")
                    .attr("font-size", "8px")
                    .attr("fill", "#888888")
                    .text(d => {
                        const value = Math.round(d);
                        return `${value}${distanceUnits === "km" ? "m" : "ft"}`;
                    });
            }
        }

        const activityLine = d3.line<any>()
            .x(d => x(d.distance))
            .y(d => y(d.accumulatedTime))
            .curve(d3.curveBasis);

        svg.append("path")
            .attr("clipPath", `url(#clip-time-${id})`)
            .datum(activityData)
            .attr("fill", "none")
            .attr("stroke", "#FF9800")
            .attr("stroke-width", 2)
            .attr("d", activityLine);

        const scenarioLine = d3.line<any>()
            .x(d => x(d.distance))
            .y(d => y(d.accumulatedTime))
            .curve(d3.curveMonotoneX);

        if (pessimisticData.length > 0) {
            svg.append("path")
                .attr("clipPath", `url(#clip-time-${id})`)
                .datum(pessimisticData)
                .attr("fill", "none")
                .attr("stroke", "#F44336")
                .attr("stroke-width", 1.5)
                .attr("stroke-dasharray", "4,4")
                .attr("d", scenarioLine);
        }

        if (optimisticData.length > 0) {
            svg.append("path")
                .attr("clipPath", `url(#clip-time-${id})`)
                .datum(optimisticData)
                .attr("fill", "none")
                .attr("stroke", "#4CAF50")
                .attr("stroke-width", 1.5)
                .attr("stroke-dasharray", "4,4")
                .attr("d", scenarioLine);
        }

        svg.append("path")
            .attr("clipPath", `url(#clip-time-${id})`)
            .datum(realisticData)
            .attr("fill", "none")
            .attr("stroke", "#2196F3")
            .attr("stroke-width", 2)
            .attr("stroke-dasharray", "5,5")
            .attr("d", scenarioLine);

        const xTicks = x.ticks(width / 80);
        const xAxis = svg.append("g")
            .attr("transform", `translate(0,${height - marginBottom})`)
            .call(g => g.append("line")
                .attr("stroke", "currentColor")
                .attr("x1", margin.left)
                .attr("x2", width - margin.right));

        xAxis.selectAll(".tick")
            .data(xTicks)
            .enter()
            .append("g")
            .attr("class", "tick")
            .attr("transform", d => `translate(${x(d)},0)`)
            .call(g => g.append("line")
                .attr("stroke", "currentColor")
                .attr("y2", 6))
            .call(g => g.append("text")
                .attr("dy", "1em")
                .attr("y", 9)
                .attr("text-anchor", "middle")
                .text(d => intl.formatMessage(
                    { id: "data.valueUnit" },
                    {
                        value: convertDistance(+d / 1000, distanceUnits),
                        unit: intl.formatMessage({ id: distanceUnits })
                    }
                )));

        const yTicks = y.ticks(height / 50);

        const yAxis = svg.append("g")
            .attr("transform", `translate(${margin.left},0)`);

        yAxis.selectAll(".tick")
            .data(yTicks)
            .enter()
            .append("g")
            .attr("class", "tick")
            .attr("transform", d => `translate(0,${y(d)})`)
            .call(g => g.append("line")
                .attr("stroke", "currentColor")
                .attr("stroke-opacity", 0.1)
                .attr("x2", width - margin.left - margin.right))
            .call(g => g.append("text")
                .attr("dx", "-0.5em")
                .attr("dy", "0.32em")
                .attr("text-anchor", "end")
                .text(d => formatTime(d)));

        const legendX = margin.left + 10;
        const legendY = margin.top + 20;
        const legendItems: { color: string; dashArray: string; label: string }[] = [
            { color: "#FF9800", dashArray: "none", label: intl.formatMessage({ id: "chart.legend.activity" }) },
            { color: "#2196F3", dashArray: "5,5", label: intl.formatMessage({ id: "plans.realistic" }) },
        ];
        if (pessimisticData.length > 0) {
            legendItems.push({ color: "#F44336", dashArray: "4,4", label: intl.formatMessage({ id: "plans.pessimistic" }) });
        }
        if (optimisticData.length > 0) {
            legendItems.push({ color: "#4CAF50", dashArray: "4,4", label: intl.formatMessage({ id: "plans.optimistic" }) });
        }

        legendItems.forEach((item, i) => {
            const itemY = legendY + i * 15;
            const lineEl = svg.append("line")
                .attr("x1", legendX)
                .attr("y1", itemY)
                .attr("x2", legendX + 20)
                .attr("y2", itemY)
                .attr("stroke", item.color)
                .attr("stroke-width", 2);
            if (item.dashArray !== "none") {
                lineEl.attr("stroke-dasharray", item.dashArray);
            }
            svg.append("text")
                .attr("x", legendX + 25)
                .attr("y", itemY + 4)
                .attr("font-size", "10px")
                .attr("fill", "currentColor")
                .text(item.label);
        });

        const hoverLine = svg.append("line").attr("class", "hoverLine");
        const hoverActivityPoint = svg.append("circle").attr("class", "hoverActivityPoint");
        const hoverRealisticPoint = svg.append("circle").attr("class", "hoverRealisticPoint");
        const hoverPessimisticPoint = svg.append("circle").attr("class", "hoverPessimisticPoint");
        const hoverOptimisticPoint = svg.append("circle").attr("class", "hoverOptimisticPoint");
        const hoverRect = svg.append("rect").attr("class", "hoverRect");
        const hoverText = svg.append("text").attr("class", "hoverText");

        const bisect = d3.bisector<{ distance: number, accumulatedTime: number }, number>(d => d.distance).left;

        const interpolateTime = (data: { distance: number; accumulatedTime: number }[], targetDistance: number): number | null => {
            if (data.length === 0) return null;
            const index = bisect(data, targetDistance);
            const p0 = data[Math.max(0, index - 1)];
            const p1 = data[Math.min(data.length - 1, index)];
            if (!p0 || !p1) return null;
            if (p0.distance === p1.distance) return p0.accumulatedTime;
            const ratio = (targetDistance - p0.distance) / (p1.distance - p0.distance);
            return p0.accumulatedTime + ratio * (p1.accumulatedTime - p0.accumulatedTime);
        };

        const mouseMove = (event: any) => {
            const mouseX = d3.pointer(event)[0];
            const xValue = x.invert(mouseX);

            setHoveredDistance?.(xValue);

            if (mouseX < margin.left || mouseX > width - margin.right) {
                return;
            }

            const activityIndex = bisect(activityData, xValue);
            const activityPoint0 = activityData[Math.max(0, activityIndex - 1)];
            const activityPoint1 = activityData[Math.min(activityData.length - 1, activityIndex)];

            if (!activityPoint0 || !activityPoint1) return;

            const activityPoint = xValue - activityPoint0.distance > activityPoint1.distance - xValue ? activityPoint1 : activityPoint0;
            const dist = activityPoint.distance;

            const realisticTime = interpolateTime(realisticData, dist);
            const pessimisticTime = pessimisticData.length > 0 ? interpolateTime(pessimisticData, dist) : null;
            const optimisticTime = optimisticData.length > 0 ? interpolateTime(optimisticData, dist) : null;

            hoverLine
                .attr("x1", x(dist))
                .attr("y1", margin.top)
                .attr("x2", x(dist))
                .attr("y2", height - marginBottom)
                .attr('stroke', '#000000')
                .attr('stroke-width', 1)
                .attr('pointer-events', 'none');

            hoverActivityPoint
                .attr("cx", x(dist))
                .attr("cy", y(activityPoint.accumulatedTime))
                .attr('r', '4')
                .attr('fill', '#FF9800')
                .attr('pointer-events', 'none');

            if (realisticTime !== null) {
                hoverRealisticPoint
                    .attr("cx", x(dist))
                    .attr("cy", y(realisticTime))
                    .attr('r', '4')
                    .attr('fill', '#2196F3')
                    .attr('pointer-events', 'none');
            }

            if (pessimisticTime !== null) {
                hoverPessimisticPoint
                    .attr("cx", x(dist))
                    .attr("cy", y(pessimisticTime))
                    .attr('r', '3')
                    .attr('fill', '#F44336')
                    .attr('pointer-events', 'none');
            }

            if (optimisticTime !== null) {
                hoverOptimisticPoint
                    .attr("cx", x(dist))
                    .attr("cy", y(optimisticTime))
                    .attr('r', '3')
                    .attr('fill', '#4CAF50')
                    .attr('pointer-events', 'none');
            }

            const timeDifference = realisticTime !== null ? activityPoint.accumulatedTime - realisticTime : 0;
            const timeDifferenceFormatted = formatTime(Math.abs(timeDifference));

            const isLessThanHalf = mouseX < width / 2;
            const hoverTextX = isLessThanHalf ? '1.25em' : '-1.25em';
            const hoverTextAnchor = isLessThanHalf ? 'start' : 'end';

            const allPointYs = [
                y(activityPoint.accumulatedTime),
                ...(realisticTime !== null ? [y(realisticTime)] : []),
                ...(pessimisticTime !== null ? [y(pessimisticTime)] : []),
                ...(optimisticTime !== null ? [y(optimisticTime)] : []),
            ];
            const higherPointY = Math.min(...allPointYs);
            const lowerPointY = Math.max(...allPointYs);
            const chartMidpointY = (margin.top + (height - marginBottom)) / 2;
            const pointsInUpperHalf = allPointYs.every(py => py < chartMidpointY);
            const tooltipPlacement = pointsInUpperHalf ? 'below' : 'above';
            const textBaseY = tooltipPlacement === 'above'
                ? higherPointY - 10
                : lowerPointY + 20;

            interface TooltipLine { text: string; color: string | null }
            const tooltipLines: TooltipLine[] = [
                {
                    text: intl.formatMessage(
                        { id: "data.distance" },
                        { value: convertDistance(xValue / 1000, distanceUnits), unit: intl.formatMessage({ id: distanceUnits }) }
                    ),
                    color: null,
                },
                {
                    text: intl.formatMessage({ id: "chart.legend.activity" }) + ": " + formatTime(activityPoint.accumulatedTime),
                    color: '#FF9800',
                },
                {
                    text: intl.formatMessage({ id: "plans.realistic" }) + ": " + (realisticTime !== null ? formatTime(realisticTime) : "-"),
                    color: '#2196F3',
                },
            ];
            if (pessimisticTime !== null) {
                tooltipLines.push({
                    text: intl.formatMessage({ id: "plans.pessimistic" }) + ": " + formatTime(pessimisticTime),
                    color: '#F44336',
                });
            }
            if (optimisticTime !== null) {
                tooltipLines.push({
                    text: intl.formatMessage({ id: "plans.optimistic" }) + ": " + formatTime(optimisticTime),
                    color: '#4CAF50',
                });
            }
            if (realisticTime !== null) {
                tooltipLines.push({
                    text: timeDifference > 0
                        ? intl.formatMessage({ id: "chart.timeDifference.behind" }, { time: timeDifferenceFormatted })
                        : intl.formatMessage({ id: "chart.timeDifference.ahead" }, { time: timeDifferenceFormatted }),
                    color: timeDifference > 0 ? '#e51f1f' : '#44ce1b',
                });
            }

            hoverText.selectAll("*").remove();

            hoverText
                .attr('fill', "#000000")
                .style('text-anchor', hoverTextAnchor)
                .data([ , ])
                .join("text")
                .call(text => text
                    .selectAll("tspan")
                    .data(tooltipLines.map(l => l.text))
                    .join("tspan")
                    .attr("x", x(dist))
                    .attr("y", textBaseY)
                    .attr('dx', hoverTextX)
                    .attr('dy', (_, i) => {
                        if (tooltipPlacement === 'above') {
                            return `${-(tooltipLines.length - 1 - i) * 1.25}em`;
                        } else {
                            return `${(i + 1) * 1.25}em`;
                        }
                    })
                    .text(d => d));

            const hoverRectSel = svg.selectAll('.hoverRect')
                .attr('fill', '#FFFFFF')
                .attr('fill-opacity', 0.9)
                .attr('pointer-events', 'none')
                .attr('stroke', '#000000')
                .attr('rx', 3)
                .attr('ry', 3);

            resizeTextRect(hoverText, hoverRectSel);

            const tspansPosition = hoverText.selectAll("tspan").nodes().map((tspan) => ({
                x: (tspan as any).getBBox().x,
                y: (tspan as any).getBBox().y,
                height: (tspan as any).getBBox().height,
            }));

            hoverRectSel.raise();
            hoverText.raise();

            tooltipLines.forEach((line, i) => {
                if (!line.color) return;
                const className = `colorRect-${i}`;
                svg.selectAll(`.${className}`)
                    .data([0])
                    .join('rect')
                    .attr('class', `colorRect ${className}`)
                    .attr('fill', line.color)
                    .attr('x', tspansPosition[i].x - 7)
                    .attr('y', tspansPosition[i].y + tspansPosition[i].height / 2 - 2)
                    .attr('width', 4)
                    .attr('height', 4)
                    .attr('pointer-events', 'none')
                    .raise();
            });
        };

        svg.on("mousemove", mouseMove);
        svg.on("mouseleave", () => {
            hoverLine.attr("stroke", "none");
            hoverActivityPoint.attr("fill", "none").attr("stroke", "none");
            hoverRealisticPoint.attr("fill", "none").attr("stroke", "none");
            hoverPessimisticPoint.attr("fill", "none").attr("stroke", "none");
            hoverOptimisticPoint.attr("fill", "none").attr("stroke", "none");
            hoverRect.attr("fill", "none").attr("stroke", "none");
            hoverText.text("");
            svg.selectAll('.colorRect').attr("fill", "none").attr("stroke", "none");
            setHoveredDistance?.(null);
        });

    }, [width, height, activityData, realisticData, pessimisticData, optimisticData, distanceUnits, intl]);

    if (activityData.length === 0 || (realisticData.length === 0 && pessimisticData.length === 0 && optimisticData.length === 0)) {
        return (
            <Box ref={containerRef} width="100%" height={height} display="flex" alignItems="center" justifyContent="center">
                <div>{intl.formatMessage({ id: "plans.noComparisonData" })}</div>
            </Box>
        );
    }

    return (
        <Box ref={containerRef} width="100%">
            <svg
                ref={svgRef}
                width={width}
                height={height}
                viewBox={`0, 0, ${width}, ${height}`}
                style={{
                    maxWidth: "100%",
                    font: "10px sans-serif",
                    overflow: "visible",
                }}
            />
        </Box>
    );
};

function resizeTextRect(text: any, rect: any) {
  if (!text || !text.node()) return;
  const { x, y, width: w, height: h } = text.node()!.getBBox();
  rect.attr("x", x - 10).attr("y", y - 5).attr("width", w + 15).attr("height", h + 10);
}

export default TimeAccumulationPlot;
